import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// ── PostLoginRedirect: reads returnUrl from sessionStorage after login ────────
function PostLoginRedirect() {
  const returnUrl = sessionStorage.getItem('returnUrl') || '/browse';
  sessionStorage.removeItem('returnUrl');
  return <Navigate to={returnUrl} replace />;
}
function ProtectedRoute({ user, allowedRoles, children }) {
  const location = useLocation();

  if (!user) {
    // Save full path + query string so we can restore it after login
    const returnUrl = location.pathname + location.search;
    if (returnUrl && returnUrl !== '/' && returnUrl !== '/login' && returnUrl !== '/tf-admin-auth') {
      sessionStorage.setItem('returnUrl', returnUrl);
    }
    return <Navigate to="/login" replace />;
  }

  // Role-based access: redirect to /browse if user lacks required role
  if (allowedRoles && !allowedRoles.some(r => user.roles.includes(r))) {
    return <Navigate to="/browse" replace />;
  }

  return children;
}
import './App.css';
import './i18n'; // Initialize i18n
import Login from './pages/Login';
import LocalLogin from './pages/LocalLogin';
import Register from './pages/Register';
import Browse from './pages/Browse';
import Upload from './pages/Upload';
import MyContent from './pages/MyContent';
import Admin from './pages/Admin';
import Analytics from './pages/Analytics';
import AuthenticateUser from './pages/AuthenticateUser';
import About from './pages/About';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import MFAVerify from './pages/MFAVerify';
import MFASetup from './pages/MFASetup';
import NDAModal from './components/NDAModal';
import FeedbackWidget from './components/FeedbackWidget';
import RequestUploadAccessWidget from './components/RequestUploadAccessWidget';
import { Toaster, toast } from 'sonner';

// Loading fallback for i18n
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    backgroundColor: '#F8F9FA'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ 
        width: '40px', 
        height: '40px', 
        border: '3px solid #E5E7EB',
        borderTopColor: '#1B5E5F',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
      }} />
      <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading...</p>
    </div>
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Session timeout in milliseconds (5 minutes)
const SESSION_TIMEOUT = 5 * 60 * 1000;
// Warning before timeout (1 minute before)
const WARNING_BEFORE_TIMEOUT = 1 * 60 * 1000;

function App() {
  const [user, setUser] = useState(null);
  const [showNDA, setShowNDA] = useState(false);
  const [ndaReaccept, setNdaReaccept] = useState(false);
  const [previousNdaVersion, setPreviousNdaVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset idle timer
  const resetIdleTimer = useCallback(() => {
    if (!user) return;
    
    lastActivityRef.current = Date.now();
    setShowTimeoutWarning(false);
    
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    
    // Set warning timer
    warningTimeoutRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      toast.warning('Your session will expire in 1 minute due to inactivity.', {
        duration: 10000,
        id: 'session-warning'
      });
    }, SESSION_TIMEOUT - WARNING_BEFORE_TIMEOUT);
    
    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      toast.error('Session expired due to inactivity. Please log in again.');
      handleLogout();
    }, SESSION_TIMEOUT);
  }, [user]);

  // Activity event listeners
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetIdleTimer();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize timer
    resetIdleTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, [user, resetIdleTimer]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Fetch user data
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(async (userData) => {
          setUser(userData);
          sessionStorage.removeItem('sunsetBannerDismissed_solutionPlay');
          
          // Check NDA status with version validation
          if (userData.roles.includes('partner')) {
            try {
              const ndaResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nda/status`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const ndaData = await ndaResponse.json();
              
              if (!ndaData.nda_accepted) {
                // User hasn't accepted current NDA version
                setShowNDA(true);
                if (ndaData.needs_reaccept) {
                  // User had accepted a previous version but needs to re-accept
                  setNdaReaccept(true);
                  setPreviousNdaVersion(ndaData.user_nda_version);
                }
              }
            } catch (error) {
              console.error('Failed to check NDA status');
              // If we can't check NDA, show the modal to be safe
              if (!userData.nda_accepted) {
                setShowNDA(true);
              }
            }
          }
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (userData, token) => {
    localStorage.setItem('token', token);
    sessionStorage.removeItem('sunsetBannerDismissed_solutionPlay');
    setUser(userData);
    
    // Check NDA status for partners
    if (userData.roles.includes('partner')) {
      try {
        const ndaResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nda/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const ndaData = await ndaResponse.json();
        
        if (!ndaData.nda_accepted) {
          setShowNDA(true);
          if (ndaData.needs_reaccept) {
            setNdaReaccept(true);
            setPreviousNdaVersion(ndaData.user_nda_version);
          }
        }
      } catch (error) {
        console.error('Failed to check NDA status');
        if (!userData.nda_accepted) {
          setShowNDA(true);
        }
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setShowNDA(false);
    setNdaReaccept(false);
    setPreviousNdaVersion(null);
  };

  const handleNDAAccept = () => {
    setShowNDA(false);
    setNdaReaccept(false);
    setPreviousNdaVersion(null);
    setUser(prev => ({ ...prev, nda_accepted: true }));
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        height: '100vh', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#FAFAFA',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E0E0E0',
          borderTopColor: '#1B5E5F',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <span style={{ color: '#5A6C7D', fontSize: '14px' }}>Loading...</span>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Router>
        <Toaster position="top-right" />
        {showNDA && (
          <NDAModal 
            onAccept={handleNDAAccept} 
            isReaccept={ndaReaccept}
            previousVersion={previousNdaVersion}
          />
        )}
        <Routes>
          <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/browse" replace />} />
          <Route path="/tf-admin-auth" element={!user ? <LocalLogin onLogin={handleLogin} /> : <Navigate to="/browse" replace />} />
          <Route path="/register" element={!user ? <Register onLogin={handleLogin} /> : <Navigate to="/browse" replace />} />
          <Route path="/authenticate-user" element={<AuthenticateUser onLogin={handleLogin} />} />
          <Route path="/mfa-verify" element={<MFAVerify onLogin={handleLogin} />} />
          <Route path="/mfa-setup" element={<MFASetup user={user} onUserUpdate={(u) => setUser(u)} />} />
          <Route path="/about" element={<About user={user} onLogout={handleLogout} />} />
          <Route path="/terms-of-use" element={<TermsOfUse user={user} onLogout={handleLogout} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy user={user} onLogout={handleLogout} />} />
          <Route path="/browse" element={
            <ProtectedRoute user={user}>
              <Browse user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          } />
          <Route path="/upload" element={
            <ProtectedRoute user={user} allowedRoles={['content_owner', 'admin']}>
              <Upload user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          } />
          <Route path="/my-content" element={
            <ProtectedRoute user={user} allowedRoles={['content_owner', 'admin']}>
              <MyContent user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute user={user} allowedRoles={['admin', 'report_viewer']}>
              <Admin user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute user={user} allowedRoles={['admin', 'report_viewer', 'content_owner']}>
              <Analytics user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          } />
          <Route path="/" element={<Navigate to="/browse" replace />} />
        </Routes>
        {/* Feedback Widget - visible when user is logged in */}
        {user && <FeedbackWidget user={user} />}
        {/* Upload Access Request Widget - visible only to eligible domain users */}
        {user && <RequestUploadAccessWidget user={user} />}
      </Router>
    </Suspense>
  );
}

export default App;
